import { AnimatePresence, motion } from 'framer-motion'
import React, { useState, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import styled, { css } from 'styled-components'
import { media } from 'utils/media'
import { Text } from 'lib/Text'
import { ReviewFormInput } from 'modules/reviews/form/mappers/inputMapper'

type Direction = 'column' | 'row'

const Wrapper = styled.div<{ direction: Direction }>`
  align-items: center;
  display: flex;
  column-gap: 16px;
  row-gap: 8px;
  flex-wrap: wrap;

  ${(props) =>
    props.direction === 'row' &&
    css`
      padding: 8px 0;
      flex-direction: row;
    `}

  ${(props) =>
    props.direction === 'column' &&
    css`
      flex-direction: column;
      justify-content: center;
      align-items: center;
    `}
`

const RadioLabel = styled.label`
  display: inline-block;
`

const RadioStar = styled.span<{ filled: boolean; disabled: boolean; size: 'large' | 'small' }>`
  box-shadow: 0 0 0 5px var(--black-20) inset;
  border-radius: 50%;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  display: block;
  height: 40px;
  width: 40px;
  transition: box-shadow 0.2s;

  ${(props) =>
    !props.disabled &&
    css`
      &:hover {
        box-shadow: 0 0 0 5px var(--black-80) inset;
      }
    `}

  ${media.phoneDown} {
    width: 36px;
    height: 36px;
  }

  ${[1, 2, 3, 4, 5].map(
    (val) =>
      css`
        ${RadioLabel}:nth-of-type(${val}) & {
          transition: box-shadow ${1.2 - val * 0.2}s;
        }
      `
  )}


  ${(props) =>
    props.filled &&
    css`
      box-shadow: 0 0 0 24px var(--black-80) inset !important;
      ${media.phoneDown} {
        box-shadow: 0 0 0 14px var(--black-80) inset;
      }
    `}}
  
    ${(props) =>
      props.size === 'small' &&
      css`
        width: 32px;
        height: 32px;
      `}
`

const InputField = styled.input`
  display: none;
  &:focus {
    outline: none;
    box-shadow: none;
  }
`

const RadioList = styled.div`
  display: inline-flex;
  gap: 8px;
`

const HintContainer = styled.div<{ direction: Direction }>`
  ${(props) =>
    props.direction === 'row' &&
    css`
      display: inline-flex;
      flex: 1 1 140px;
    `}
`

const HintText = styled(motion.div)``

const hintForRating = {
  1: '1 out of 5 means it was a big let down.',
  2: '2 out of 5 means it was unremarkable.',
  3: '3 out of 5 means it was just okay.',
  4: '4 out of 5 means it was really great.',
  5: '5 out of 5 means you absolutely loved it.'
}

interface Props {
  hint?: string
  direction?: Direction
  onRatingChange?: (rating: number) => void
  disabled?: boolean
  showHint?: boolean
  size?: 'small' | 'large'
}

export const StarSelector = ({ hint: defaultHint, direction = 'row', onRatingChange, disabled, showHint = true, size = 'large' }: Props) => {
  const { register, watch, setValue } = useFormContext<ReviewFormInput>()
  const rating = watch('stars')
  const [hint, setHint] = useState<number | null>(null)

  useEffect(() => {
    if (rating) {
      setHint(+rating)
    } else {
      setHint(null)
    }
  }, [rating])

  const handleMouseEnter = (val: number) => {
    if (!disabled) {
      setHint(val)
    }
  }

  const handleMouseLeave = () => {
    if (!disabled) {
      if (rating) {
        setHint(+rating)
      } else {
        setHint(null)
      }
    }
  }

  return (
    <Wrapper direction={direction}>
      <RadioList onMouseLeave={() => handleMouseLeave()}>
        {[1, 2, 3, 4, 5].map((r) => (
          <RadioLabel key={r} onMouseEnter={() => handleMouseEnter(r)}>
            <InputField
              disabled={disabled}
              type="radio"
              // Slight hack that lets the user deselect a rating
              onClick={(e: any) => {
                if (e.target.value === rating) {
                  setValue('stars', null)
                  e.target.checked = false
                }
              }}
              {...register('stars', {
                required: 'You must provide a rating for your review.',
                onChange: (e) => {
                  onRatingChange?.(parseInt(e.target.value))
                }
              })}
              value={r}
            />
            <RadioStar filled={rating ? +rating >= r : false} disabled={disabled} size={size} />
          </RadioLabel>
        ))}
      </RadioList>

      {showHint && (
        <AnimatePresence>
          <HintContainer direction={direction}>
            {hint === null && defaultHint && (
              <HintText
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                layout
                transition={{ duration: 0.5, type: 'tween', ease: 'anticipate' }}>
                <Text size="pxs" color="black-60">
                  {defaultHint}
                </Text>
              </HintText>
            )}

            {[1, 2, 3, 4, 5].map(
              (r) =>
                r === hint && (
                  <HintText
                    key={r}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    layout
                    transition={{ duration: 0.5, type: 'tween', ease: 'anticipate' }}>
                    <Text size="pxs" color="black-60">
                      {hintForRating[r as keyof typeof hintForRating]}
                    </Text>
                  </HintText>
                )
            )}
          </HintContainer>
        </AnimatePresence>
      )}
    </Wrapper>
  )
}
